import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { saveAs } from 'file-saver';
import {
  CompanyVendor,
  ExemptVendor,
  File,
  ProposalRequest,
  ProposalRequestVendor,
  Vendor
} from 'src/app/classes/models';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ProposalRequestService } from 'src/app/services/proposal-request.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-proposal-request',
  templateUrl: './proposal-request.component.html',
  styleUrls: ['./proposal-request.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ProposalRequestComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private CompanyVendorService: CompanyVendorService,
    private ProposalRequestService: ProposalRequestService,
    private Route: ActivatedRoute,
    private ToastService: ToastService
  ) {}

  Vendor: Vendor | ExemptVendor | null = null;
  ProposalRequestVendor: ProposalRequestVendor | null = null;
  ProposalRequest: ProposalRequest | null = null;

  proposalRequestCard: {
    label: string;
    value: string | number | null | undefined;
  }[] = [];
  proposalRequestLineItems: Map<string, string> = new Map();
  proposalLineItems: Set<{ label: string; value: string | number }> = new Set();
  proposalRequestFiles: File[] = [];
  proposalFiles: File[] = [];

  downloadFile(File: File, type: 'ProposalRequestFile' | 'ProposalFile') {
    const { id: FileId } = File;
    if (!FileId) {
      throw this.ToastService.addErrorToast('Error', 'File is missing id.', {
        returnError: true
      });
    }

    if (type === 'ProposalRequestFile') {
      this.ProposalRequestService.getProposalRequestVendor({
        attributes: JSON.stringify(['id']),
        ProposalRequest: JSON.stringify({
          options: { required: true, duplicating: false },
          attributes: ['id'],
          ProposalRequestFile: {
            options: { required: true, duplicating: false },
            attributes: ['id'],
            File: {
              id: FileId,
              options: { required: true, duplicating: false }
            }
          }
        })
      }).subscribe({
        next: (ProposalRequestVendor) => {
          if (!ProposalRequestVendor) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Vendor information for File download.',
              { returnError: true }
            );
          }

          const { ProposalRequest } = ProposalRequestVendor;

          if (!ProposalRequest) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Proposal information for File download.',
              { returnError: true }
            );
          }

          const { id: ProposalRequestId, ProposalRequestFiles } =
            ProposalRequest;

          if (!ProposalRequestId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing proposal request id.',
              { returnError: true }
            );
          }

          if (!ProposalRequestFiles || ProposalRequestFiles.length !== 1) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Proposal File information.',
              { returnError: true }
            );
          }

          const { File } = ProposalRequestFiles[0];

          if (!File) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find File information.',
              { returnError: true }
            );
          }

          const { filepath } = File;

          if (!filepath) {
            throw this.ToastService.addErrorToast(
              'Error',
              'No file path found.',
              { returnError: true }
            );
          }

          this.ProposalRequestService.getProposalRequestFileS3PresignedUrl(
            ProposalRequestId,
            FileId
          ).subscribe({
            next: ({ presignedUrl, filename }) => {
              saveAs(presignedUrl, filename);
            }
          });
        }
      });
    }

    if (type === 'ProposalFile') {
      this.ProposalRequestService.getProposalRequestVendor({
        attributes: JSON.stringify(['id']),
        Proposal: JSON.stringify({
          attributes: ['id'],
          options: { required: true, duplicating: false },
          ProposalFile: {
            attributes: ['id'],
            options: { required: true, duplicating: false },
            File: {
              id: FileId,
              options: { required: true, duplicating: false }
            }
          }
        })
      }).subscribe({
        next: (ProposalRequestVendor) => {
          if (!ProposalRequestVendor) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Vendor information for File download.',
              { returnError: true }
            );
          }

          const { Proposal } = ProposalRequestVendor;

          if (!Proposal) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Proposal information for File download.',
              { returnError: true }
            );
          }

          const { id: ProposalId, ProposalFiles } = Proposal;

          if (!ProposalId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing proposal id.',
              { returnError: true }
            );
          }

          if (!ProposalFiles || ProposalFiles.length !== 1) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Proposal File information.',
              { returnError: true }
            );
          }

          const ProposalFile = ProposalFiles[0];

          const { File } = ProposalFile;

          if (!File) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find File information.',
              { returnError: true }
            );
          }

          const { filepath } = File;

          if (!filepath) {
            throw this.ToastService.addErrorToast(
              'Error',
              'No file path found.',
              { returnError: true }
            );
          }

          this.ProposalRequestService.getProposalFileS3PresignedUrl(
            ProposalId,
            FileId
          ).subscribe({
            next: ({ presignedUrl, filename }) => {
              saveAs(presignedUrl, filename);
            }
          });
        }
      });
    }
  }

  ngOnInit(): void {
    const paramMap: ParamMap = this.Route.snapshot.paramMap;
    const VendorId = Number(paramMap.get('VendorId')) || null;
    const ExemptVendorId = Number(paramMap.get('ExemptVendorId')) || null;
    const ProposalRequestId = Number(paramMap.get('ProposalRequestId')) || null;
    const companyVendorQuery = {};
    const proposalRequestQuery = {
      ProposalRequestId,
      Proposal: JSON.stringify({
        ProposalFile: {
          File: {}
        }
      }),
      ProposalRequest: JSON.stringify({
        Site: {},
        Address: {},
        ProposalRequestFile: {
          File: {}
        }
      })
    };

    if (VendorId) {
      Object.assign(companyVendorQuery, {
        VendorId,
        Vendor: JSON.stringify({})
      });

      Object.assign(proposalRequestQuery, { VendorId });
    }

    if (ExemptVendorId) {
      Object.assign(companyVendorQuery, {
        ExemptVendorId,
        ExemptVendor: JSON.stringify({})
      });

      Object.assign(proposalRequestQuery, { ExemptVendorId });
    }

    this.ProposalRequestService.getProposalRequestVendor(
      proposalRequestQuery
    ).subscribe({
      next: (ProposalRequestVendor: ProposalRequestVendor) => {
        if (!ProposalRequestVendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No ProposalRequestVendor Record Found.',
            { returnError: true }
          );
        }

        this.ProposalRequestVendor = ProposalRequestVendor;

        const { ProposalRequest, Proposal } = this.ProposalRequestVendor;

        if (!ProposalRequest) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No ProposalRequest Record Found.',
            { returnError: true }
          );
        }

        this.ProposalRequest = ProposalRequest;

        const { ProposalRequestFiles } = this.ProposalRequest;

        const proposalRequestLineItems = Object.entries(
          this.ProposalRequest
        ).filter(([key, value]) => key.includes('lI'));
        proposalRequestLineItems.forEach((item, index) =>
          this.proposalRequestLineItems.set(`Line Item ${index + 1}: `, item[1])
        );

        if (ProposalRequestFiles && ProposalRequestFiles.length > 0) {
          ProposalRequestFiles.forEach((prFile) => {
            const { File } = prFile;

            if (File) {
              this.proposalRequestFiles.push(File);
            }
          });
        }

        if (Proposal) {
          const { ProposalFiles } = Proposal;

          if (ProposalFiles && ProposalFiles.length > 0) {
            ProposalFiles.forEach((pFile) => {
              const { File } = pFile;

              if (File) {
                this.proposalFiles.push(File);
              }
            });
          }

          const proposalLineItems = Object.entries(Proposal).filter(
            ([key, value]) => key.includes('lI') && value !== null
          );

          proposalLineItems.forEach(([key, value], index) =>
            this.proposalLineItems.add({
              label:
                this.proposalRequestLineItems.get(`Line Item ${index + 1}: `)! +
                ': ',
              value
            })
          );
        }

        this._cdr.markForCheck();
      }
    });

    this.CompanyVendorService.getCompanyVendor(companyVendorQuery).subscribe({
      next: (CompanyVendor: CompanyVendor) => {
        if (!CompanyVendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No CompanyVendor Record Found.',
            { returnError: true }
          );
        }

        const { Vendor, ExemptVendor } = CompanyVendor;

        if (Vendor) {
          this.Vendor = Vendor;
        }

        if (ExemptVendor) {
          this.Vendor = ExemptVendor;
        }

        this._cdr.markForCheck();
      }
    });
  }
}
